import { useFormState } from "react-hook-form";
import useFormFieldValidationHit from "../../../hooks/useValidatorFieldsProperty";
import usePropertyHelpTypeLocations from "../../../hooks/helps/usePropertyHelpTypeLocations";
import { useMemo } from "react";
import translatePropertyLabels from "../../../util/helps/translatePropertyLabels";
import { PropertyFormSchema } from "../../../util/schemas/property-schema/propertySchema";

type FormStatus = "loading" | "error" | "success" | "info";

interface FormError {
  label: string;
  value: string;
  meta?: any;
}

interface UseStatusIndicatorProps {
  schema: typeof PropertyFormSchema;
  fieldNames?: string[];
  meta?: any;
}

const useFormStatusIndicator = ({
  schema,
  fieldNames = [],
  meta
}: UseStatusIndicatorProps) => {
  const { errors, isValidationRunning } = useFormFieldValidationHit({
    schema,
    fieldNames,
    parserNameFn: translatePropertyLabels
  });

  const { isDirty, isSubmitted } = useFormState();
  const { isEdit, isCreate } = usePropertyHelpTypeLocations();

  const formattedErrors = useMemo<FormError[]>(
    () =>
      Object.keys(errors).map(key => ({
        label: key,
        value: errors[key],
        meta
      })),
    [errors, meta]
  );

  const hasErrors = useMemo(() => formattedErrors.length > 0, [formattedErrors]);

  const status = useMemo<FormStatus>(() => {
    if (isValidationRunning) return "loading";

    if (isEdit) {
      return hasErrors ? "error" : "success";
    }

    if (isCreate) {
      if (hasErrors) {
        return isDirty ? "error" : isSubmitted ? "error" : "info";
      }
      return isDirty ? "success" : "info";
    }

    return "info";
  }, [hasErrors, isDirty, isValidationRunning, isSubmitted, isEdit, isCreate]);

  return { status, formattedErrors };
};

export default useFormStatusIndicator;
